import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const links = [
    { id: "about", label: "About" },
    { id: "trilogy", label: "Trilogy" },
    { id: "movies", label: "Films" },
    { id: "engineering", label: "Engineering" },
    { id: "contact", label: "Contact" },
];

export default function Nav({ activeSection }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isDark = activeSection === "trilogy" || activeSection === "movies";

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                scrolled
                    ? isDark
                        ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
                        : "bg-white/70 backdrop-blur-xl border-b border-black/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                <a
                    href="#hero"
                    data-testid="nav-logo"
                    className={`font-display font-black text-xl tracking-tight ${
                        isDark ? "text-white" : "text-zinc-950"
                    }`}
                >
                    RANE<span className="text-[#FF5500]">.</span>DYNAMICS
                </a>

                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <a
                            key={l.id}
                            href={`#${l.id}`}
                            data-testid={`nav-link-${l.id}`}
                            className={`px-4 py-2 text-sm font-medium tracking-wide rounded-full transition-colors duration-300 ${
                                activeSection === l.id
                                    ? isDark
                                        ? "bg-white/10 text-white"
                                        : "bg-black/5 text-zinc-950"
                                    : isDark
                                      ? "text-zinc-300 hover:text-white"
                                      : "text-zinc-600 hover:text-zinc-950"
                            }`}
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <a
                    href="#contact"
                    data-testid="nav-cta"
                    className="hidden md:inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#E64D00] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
                >
                    Get In Touch
                </a>

                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    className={`md:hidden p-2 rounded-full ${isDark ? "text-white" : "text-zinc-950"}`}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className={`md:hidden border-t ${
                        isDark ? "bg-black/95 border-white/10" : "bg-white/95 border-black/5"
                    }`}
                >
                    <div className="px-6 py-6 flex flex-col gap-1">
                        {links.map((l) => (
                            <a
                                key={l.id}
                                href={`#${l.id}`}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-link-${l.id}`}
                                className={`px-4 py-3 text-base font-medium rounded-xl ${
                                    isDark
                                        ? "text-zinc-200 hover:bg-white/5"
                                        : "text-zinc-800 hover:bg-black/5"
                                }`}
                            >
                                {l.label}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
